import { useEffect, useState, useRef, memo } from "react";
import { Entity } from "../model/entities";
import { UUID } from "../model/uuid";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	List,
} from "@mui/material";
import { Situation as SituationIntermediate } from "../structure/intermediates";
import { MentionModel } from "../communication";
import { arrowSVG, crossSVG } from "../constants";
type Referent = Entity | SituationIntermediate;

interface ReferentProps {
	referent: Referent;
	focusedReferent: number | undefined;
	activeStructureIds: [UUID, UUID, MentionModel][];
	activateStructureIds: (
		uuids: UUID[],
		tokenizationUUIDs: UUID[],
		mentionModel: MentionModel
	) => void;
	index: number;
	deleteReferent: (uuid: UUID) => void;
	selectReferentByIndex: (index?: number | "up" | "down") => void;
	addMentionToReferent: (referent: Referent, mentionId: UUID[]) => void;
	removeMentionFromReferent: (referent: Referent, mentionId: UUID) => void;
	spanMap: Map<string, string>;
	modalExpanded: boolean;
	currentMentionModel: MentionModel;
}

function Referent(props: ReferentProps) {
	const [focused, setFocused] = useState<boolean>(
		props.index === props.focusedReferent
	);
	const [opened, setOpened] = useState<boolean>(false);
	const [mentionExists, setMentionExists] = useState<boolean | undefined>(
		props.activeStructureIds.length !== 0 &&
			props.referent.mentionIdList !== undefined
			? !!~props.referent.mentionIdList!.findIndex(
					(m) =>
						m.uuidString ===
						props.activeStructureIds[0][0]?.uuidString
			  )
			: undefined
	); // Equal to true when this referent's mentionIdList contains the activeStructureId

	const referentEl = useRef<HTMLDivElement>(null);
	const firstRun = useRef<boolean>(true); //TODO: Pull it up?

	// setFocused when props change
	useEffect(() => {
		setFocused(props.focusedReferent === props.index);
	}, [props.focusedReferent]);

	// Focus or blur this element when focused state changes
	useEffect(() => {
		if (focused) {
			referentEl.current?.focus();
		} else {
			referentEl.current?.blur();
		}
	}, [props.activeStructureIds]);

	useEffect(() => {
		if (focused) {
			referentEl.current?.focus();
		} else {
			referentEl.current?.blur();
			setOpened(false);
		}
	}, [focused]);

	useEffect(() => {
		if (focused && !props.modalExpanded) {
			referentEl.current?.focus();
		}
	}, [props.modalExpanded]);

	// Adds and remove event listeners when they need to be updated
	useEffect(() => {
		const ref = referentEl.current;
		ref?.addEventListener("keydown", onKeyDownHandler);
		return () => ref?.removeEventListener("keydown", onKeyDownHandler);
	}, [props.selectReferentByIndex, mentionExists]);

	// Update state mentionExists when props.referent or props.activeStructureIds changes
	useEffect(() => {
		const mentionExists = props.activeStructureIds.length
			? !!~props.referent.mentionIdList!.findIndex(
					(m) =>
						m.uuidString ===
						props.activeStructureIds[0][0]?.uuidString
			  )
			: undefined;
		setMentionExists(mentionExists);
	}, [props.activeStructureIds, props.referent.mentionIdList]);

	// TODO: modifying props directly
	useEffect(() => {
		if (!firstRun.current) {
			props.referent.mentionIdList = props.referent.mentionIdList!.filter(
				(id) => props.spanMap.has(id.uuidString)
			);
		} else {
			firstRun.current = false;
		}
	});

	const deleteButtonClickHandler = () => {
		props.deleteReferent(props.referent.uuid);
	};

	const onKeyDownHandler = (e: KeyboardEvent) => {
		if (e.key === "ArrowUp") {
			e.preventDefault();
			props.selectReferentByIndex("up");
		} else if (e.key === "ArrowDown") {
			e.preventDefault();
			props.selectReferentByIndex("down");
		} else if (
			e.key === "Enter" &&
			props.activeStructureIds.length &&
			!(e.ctrlKey || e.metaKey)
		) {
			props.addMentionToReferent(
				props.referent,
				props.activeStructureIds.map(([uuid1]) => uuid1)
			);
		} else if (e.key === "ArrowRight") {
			setOpened(true);
		} else if (e.key === "ArrowLeft") {
			setOpened(false);
		}
	};

	const name = props.referent.canonicalName;

	const countText = `${props.referent.mentionIdList!.length} mention${
		props.referent.mentionIdList!.length === 1 ? "" : "s"
	}`;

	let referentClassName = `referent ${opened ? " open" : ""}${
		focused ? " focused" : ""
	}${mentionExists ? " highlighted" : ""}`;

	if (props.currentMentionModel === "entity") {
		referentClassName += " entity";
	} else {
		referentClassName += " situation";
	}

	const mentionListClassName = `mention-list ${opened ? " open" : " closed"}`;

	const mentions = props.referent.mentionIdList!.map((id) => {
		const match = props.activeStructureIds.some(
			(sId) => sId[0]?.uuidString === id.uuidString
		);
		const className = `mention${match ? " active" : ""}`;

		// change parameters for props.activateStructureIds
		return (
			<div
				className={className + " referent-mention"}
				key={"mention" + id.uuidString}>
				{props.spanMap.get(id.uuidString)}
				<div
					className="delete-mention"
					onClick={() =>
						props.removeMentionFromReferent(props.referent, id)
					}
				>{crossSVG}</div>
			</div>
		);
	});

	return (
		<>
			<Accordion
				square
				disableGutters
				expanded={opened}
				sx={{
					width: "100%",
					boxShadow: "none",
				}}>
				<AccordionSummary
					sx={{
						"padding": 0,
						"&.Mui-focusVisible": {
							background: "none",
						},

						"> .MuiAccordionSummary-content": {
							margin: "0",
						},
					}}>
					<div
						className={referentClassName}
						ref={referentEl}
						onClick={(e) => {
							e.stopPropagation();
							if (focused) {
								props.addMentionToReferent(
									props.referent,
									props.activeStructureIds.map(
										([uuid1]) => uuid1
									)
								);
							} else {
								props.selectReferentByIndex(props.index);
							}
						}}
						tabIndex={1}>
						<p className="name">{name}</p>
						<p className="item-count">{countText}</p>
						<div
							className="delete"
							onClick={deleteButtonClickHandler}
						>{crossSVG}</div>
						<div
							className={`dropdown${
								mentions.length ? "" : " invisible"
							}`}
							onClick={() => {
								setOpened((open) => !open);
							}}
						>{arrowSVG}</div>
					</div>
				</AccordionSummary>
				<AccordionDetails
					sx={{
						padding: "0",
					}}
					className={mentionListClassName}>
					<List
						className={mentionListClassName}
						sx={{
							padding: "0",
						}}>
						{mentions}
					</List>
				</AccordionDetails>
			</Accordion>
		</>
	);
}

export default memo(Referent);
